import {
    ADDITIONAL_EMAILS_RESOURCE_NAME,
    MAX_CHATS_RESOURCE_NAME,
    TELEGRAM_CHATS_RESOURCE_NAME,
    WEBHOOKS_RESOURCE_NAME,
} from 'shared/config/model/urls';
import {AUTO_SELECT_ID} from 'shared/lib/constants';

import {DefaultValues} from 'react-hook-form';
import {t} from '@lingui/core/macro';

import {buildBatchEditPayload} from 'features/http/lib/batchEditFields';
import {headersToRows, rowsToHeaders} from 'features/http/lib/requestHeaders';
import CreateHttpMonitorType from 'features/http/model/HttpMonitor/CreateHttpMonitorType';
import HttpMonitorPayload from 'features/http/model/HttpMonitor/HttpMonitorPayload';
import IHttpMonitorOutput from 'features/http/model/HttpMonitor/IHttpMonitor';
import {IUserOutput} from 'entities/user';
import {
    getDomainChecksDefaults,
    buildCommonBatchRequest,
    IBatchOperationRequest,
    IBuildBatchRequestParams,
    normalizeTags,
} from 'entities/monitor';
import {idsToIri, normalizeIds} from 'shared/api/iriHelper';

const getLocationsDefault = (item: IHttpMonitorOutput | undefined, freeTariff: boolean) => (
    item?.locations && item.locations.length > 0
        ? normalizeIds(item.locations)
        : (!freeTariff ? [AUTO_SELECT_ID] : [])
);

export function getHttpMonitorFormDefaults(
    item: IHttpMonitorOutput | undefined,
    user: IUserOutput | null,
    freeTariff: boolean,
    isEnglish: boolean,
): DefaultValues<CreateHttpMonitorType> {
    const isNew = !item;
    const isNewForChecks = isNew && !isEnglish;
    const isNotifyByEmailChecked = user?.email_confirmed && (item?.notify_by_email || isNewForChecks);
    const headerRows = headersToRows(item?.headers);

    return {
        ...(isNew ? getDomainChecksDefaults(user) : {}),
        parse_errors: item?.parse_errors,
        screenshot_on_error: item?.screenshot_on_error,
        frequent_checking: item?.frequent_checking,
        method: item?.method ?? 'HEAD',
        parse_errors_type: item?.parse_errors_type ?? 'json',
        notify_by_email: isNotifyByEmailChecked,
        telegram_chats: normalizeIds(item?.telegram_chats),
        max_chats: normalizeIds(item?.max_chats),
        webhooks: normalizeIds(item?.webhooks),
        additional_emails: normalizeIds(item?.additional_emails),
        http_request_timeout: item?.http_request_timeout ?? 30,
        notify_email_confirmed_delay: item?.notify_email_confirmed_delay ?? 0,
        monitor_content_enabled: item?.monitor_content_enabled ?? false,
        monitor_content_data: item?.monitor_content_data,
        monitor_content_logic: item?.monitor_content_logic,
        monitor_content_notify_logic: item?.monitor_content_notify_logic,
        auth_enabled: item?.auth_enabled,
        headers: headerRows.length > 0 ? headerRows : [{name: '', value: ''}],
        tags: item?.tags?.map(tag => tag.label) || [],
        locations: getLocationsDefault(item, freeTariff),
        threshold: freeTariff ? 1 : (item?.threshold ?? 3),
    };
}

export function getHttpBatchCreateDefaults(
    user: IUserOutput | null,
    freeTariff: boolean,
    isEnglish: boolean,
): DefaultValues<CreateHttpMonitorType> {
    return {
        ...getDomainChecksDefaults(user),
        method: 'HEAD',
        notify_by_email: user?.email_confirmed && !isEnglish,
        http_request_timeout: 30,
        parse_errors: false,
        parse_errors_type: 'json',
        parse_errors_pattern: '',
        screenshot_on_error: false,
        frequent_checking: false,
        monitor_content_enabled: false,
        auth_enabled: false,
        headers: [{name: '', value: ''}],
        locations: getLocationsDefault(undefined, freeTariff),
        threshold: freeTariff ? 1 : 3,
    };
}

export function getHttpBatchOperationsDefaults(
    user: IUserOutput | null,
    freeTariff: boolean,
): DefaultValues<CreateHttpMonitorType> {
    return {
        frequent_checking: false,
        parse_errors: false,
        screenshot_on_error: false,
        monitor_content_enabled: false,
        auth_enabled: false,
        notify_by_email: user?.email_confirmed,
        method: 'HEAD',
        parse_errors_type: 'json',
        parse_errors_pattern: '',
        headers: [{name: '', value: ''}],
        headers_mode: 'replace',
        locations: !freeTariff ? [AUTO_SELECT_ID] : [],
        threshold: freeTariff ? 1 : 3,
    };
}

function buildHttpMonitorBasePayload(
    {locations, headers, ...rest}: CreateHttpMonitorType,
): HttpMonitorPayload {
    const cleanLocations = (locations || []).filter(loc => loc !== AUTO_SELECT_ID);
    const data: HttpMonitorPayload = {
        ...rest,
        headers: rowsToHeaders(headers),
        locations: idsToIri(cleanLocations, 'locations'),
    };

    data.telegram_chats = idsToIri(data.telegram_chats, TELEGRAM_CHATS_RESOURCE_NAME);
    data.max_chats = idsToIri(data.max_chats, MAX_CHATS_RESOURCE_NAME);
    data.additional_emails = idsToIri(data.additional_emails, ADDITIONAL_EMAILS_RESOURCE_NAME);

    if (data.http_request_timeout === 0) {
        data.http_request_timeout = 1;
    }

    return data;
}

export function buildHttpMonitorPayload({tags, ...rest}: CreateHttpMonitorType): HttpMonitorPayload {
    const data = buildHttpMonitorBasePayload(rest as CreateHttpMonitorType);

    data.webhooks = idsToIri(data.webhooks, WEBHOOKS_RESOURCE_NAME);
    data.tags = tags ? normalizeTags(tags) : [];

    return data;
}

export function buildHttpBatchCreatePayload(values: CreateHttpMonitorType): HttpMonitorPayload {
    return buildHttpMonitorBasePayload(values);
}

export function buildHttpBatchOperationRequest({
    actionValue,
    formData,
    addedFields,
    selectedIds,
}: IBuildBatchRequestParams<CreateHttpMonitorType>): IBatchOperationRequest {
    if (actionValue === 'edit') {
        return {method: 'PUT', data: buildBatchEditPayload(addedFields, formData, selectedIds)};
    }

    return buildCommonBatchRequest(actionValue, selectedIds, formData.tags);
}

export function getHttpBatchModalHeader(actionValue?: string): string {
    switch (actionValue) {
        case 'start':
            return t`Массовое включение сайтов`;
        case 'stop':
            return t`Массовая остановка сайтов`;
        case 'edit':
            return t`Массовое изменение сайтов`;
        case 'delete':
            return t`Массовое удаление сайтов`;
        case 'tags':
            return t`Массовое добавление тегов`;
        default:
            return t`Массовые операции`;
    }
}
